import { useEffect, useState, useCallback, useMemo, memo } from "react";
import { useDispatch } from "react-redux";
import { Input, DatePicker, Alert, Pagination, Spin } from "antd";
import { ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import dayjs from "dayjs";

import Headers from "../../../redux/Actions/Headers/Header";
import service from "../../../auth/FetchInterceptor";
import PrefixPath from "../../../config/AppConfig";
import DynamicTable from "../../../Components/Table/Table";
import TableCard from "../../../Components/TableCard/TableCard";
import Badge from "../../../Components/Badge/Badge";
import styles from "./SMSDetailsView.module.scss";
import { formatPhone } from "../../../utils/Formatters";

const { TextArea } = Input;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 750);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return isMobile;
};

const ResponsiveStrategy = ({ Mobile, Desktop, ...rest }) => {
  const isMobile = useIsMobile();
  return isMobile ? <Mobile {...rest} /> : <Desktop {...rest} />;
};

const SORT_FIELDS = [
  { value: "lastName",    label: "Last Name" },
  { value: "firstName",   label: "First Name" },
  { value: "phoneNumber", label: "Cell Number" },
  { value: "residentKey", label: "Account Number" },
  { value: "address",     label: "Address" },
  { value: "block",       label: "Block" },
  { value: "lot",         label: "Lot" },
  { value: "sendStatus",  label: "Status" },
];

const SEND_STATUS_MAP = {
  0: { label: "Pending", variant: "yellow" },
  1: { label: "Sent", variant: "blue" },
  2: { label: "Failed", variant: "red" },
  3: { label: "Delivered", variant: "green" },
};

const getSendStatusInfo = (status) =>
  SEND_STATUS_MAP[status] ?? { label: `Unknown (${status})`, variant: "gray" };

const DesktopView = memo(({
  filteredRows,
  antdColumns,
  currentPage,
  pageSize,
  handleTableChange,
  onRow,
}) => (
  <div className={styles.tableScroll}>
    <DynamicTable
      rowKey={(_, idx) => idx}
      needDefaultWidth
      columns={antdColumns}
      data={filteredRows}
      pagination={{
        current: currentPage,
        pageSize,
        total: filteredRows.length,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        showTotal: (total, range) =>
          total ? `${range[0]}-${range[1]} of ${total} rows` : "0 rows",
      }}
      onChange={handleTableChange}
      onRow={onRow}
    />
  </div>
));

DesktopView.displayName = "SMSDetailsDesktopView";

const MobileView = memo(({
  rows,
  pagination,
  currentPage,
  pageSize,
  handleCustomPageChange,
  handlePageSize,
  handleSorting,
  sortModel,
  makeFields,
}) => {
  const sortField = sortModel?.[0]?.field || "";
  const sortDir   = sortModel?.[0]?.sort  || "asc";

  const handleSortFieldChange = (e) => {
    const field = e.target.value;
    handleSorting(field ? [{ field, sort: sortDir || "asc" }] : []);
  };

  const handleSortDirChange = (e) => {
    if (!sortField) return;
    handleSorting([{ field: sortField, sort: e.target.value }]);
  };

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel id="details-sort-field">Sort by</InputLabel>
            <Select
              labelId="details-sort-field"
              label="Sort by"
              value={sortField}
              onChange={handleSortFieldChange}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {SORT_FIELDS.map((f) => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={!sortField}>
            <InputLabel id="details-sort-dir">Direction</InputLabel>
            <Select
              labelId="details-sort-dir"
              label="Direction"
              value={sortDir}
              onChange={handleSortDirChange}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {!rows?.length ? (
        <div className="text-center p-4">No recipients found.</div>
      ) : (
        <div className="table-card-stack">
          {rows.map((row, idx) => (
            <Box key={idx}>
              <TableCard id={`recipient-card-${idx}`} fields={makeFields(row)} />
            </Box>
          ))}
        </div>
      )}

      <div className={styles.pagination}>
        <span className={styles.paginationText}>
          Showing {Math.min((currentPage - 1) * pageSize + 1, pagination.total)}–
          {Math.min(currentPage * pageSize, pagination.total)} of {pagination.total} rows
        </span>
        <select
          value={pageSize}
          onChange={handlePageSize}
          className={styles.paginationSizeSelect}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={pagination.total}
          onChange={handleCustomPageChange}
          showSizeChanger={false}
        />
      </div>
    </Box>
  );
});

MobileView.displayName = "SMSDetailsMobileView";

const SMSDetailsView = () => {
  const dispatch    = useDispatch();
  const navigate    = useNavigate();
  const { id: messageId } = useParams();

  const [sms,             setSms]             = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [recipientSearch, setRecipientSearch] = useState("");

  const [sortModel,   setSortModel]   = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize,    setPageSize]    = useState(10);

  useEffect(() => {
    dispatch(
      Headers.setHeader({
        title: (
          <div className={styles.headerTitle}>
            <ArrowLeftOutlined
              className={styles.backIcon}
              onClick={() => navigate(-1)}
            />
            <span>SMS Blast</span>
          </div>
        ),
      })
    );
    return () => dispatch(Headers.resetHeader());
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!messageId) {
      setError("Invalid SMS message identifier.");
      setLoading(false);
      return;
    }
    service
      .get(`${PrefixPath.SMS_API_BASE_URL}Message/${messageId}`)
      .then((res) => {
        if (res?.isSuccess || res?.IsSuccess) {
          setSms(res.value);
        } else {
          setError(res?.message || "Failed to load message details.");
        }
      })
      .catch(() => setError("Error loading message details."))
      .finally(() => setLoading(false));
  }, [messageId]);

  const antdColumns = useMemo(() => {
    const sortOrderFor = (field) => {
      const first = sortModel?.[0];
      if (!first || first.field !== field) return undefined;
      return first.sort === "asc" ? "ascend" : "descend";
    };
    return [
      { title: "Last Name",    dataIndex: "lastName",    key: "lastName",    width: 160, sorter: true, sortOrder: sortOrderFor("lastName"),    render: (v) => v ?? "-" },
      { title: "First Name",   dataIndex: "firstName",   key: "firstName",   width: 160, sorter: true, sortOrder: sortOrderFor("firstName"),   render: (v) => v ?? "-" },
      { title: "Cell Number",  dataIndex: "phoneNumber", key: "phoneNumber", width: 160, sorter: true, sortOrder: sortOrderFor("phoneNumber"), render: (v) => formatPhone(v) },
      { title: "Account Number", dataIndex: "residentKey", key: "residentKey", width: 160, sorter: true, sortOrder: sortOrderFor("residentKey"), render: (v) => v ?? "-" },
      { title: "Address",      dataIndex: "address",     key: "address",     width: 160, sorter: true, sortOrder: sortOrderFor("address"),     render: (v) => v ?? "-" },
      { title: "Block",        dataIndex: "block",       key: "block",       width: 160, sorter: true, sortOrder: sortOrderFor("block"),       render: (v) => v ?? "-" },
      { title: "Lot",          dataIndex: "lot",         key: "lot",         width: 160, sorter: true, sortOrder: sortOrderFor("lot"),         render: (v) => v ?? "-" },
      {
        title: "Status", dataIndex: "sendStatus", key: "sendStatus", width: 160,
        sorter: true, sortOrder: sortOrderFor("sendStatus"),
        render: (status) => {
          const { label, variant } = getSendStatusInfo(status);
          return <Badge text={label} variant={variant} />;
        },
      },
    ];
  }, [sortModel]);

  const filteredAndSortedRows = useMemo(() => {
    let rows = [...(sms?.recipients || [])];

    if (recipientSearch) {
      const needle = recipientSearch.toLowerCase();
      rows = rows.filter((r) =>
        [r.firstName, r.lastName, r.phoneNumber, r.residentKey, r.address, r.block, r.lot, getSendStatusInfo(r.sendStatus).label]
          .map((v) => v ?? "")
          .join(" ")
          .toLowerCase()
          .includes(needle)
      );
    }

    const first = sortModel?.[0];
    if (first?.field && first?.sort) {
      const dir = first.sort === "asc" ? 1 : -1;
      rows.sort((a, b) => {
        const av = a[first.field] ?? "";
        const bv = b[first.field] ?? "";
        if (av < bv) return -1 * dir;
        if (av > bv) return  1 * dir;
        return 0;
      });
    }

    return rows;
  }, [sms, recipientSearch, sortModel]);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedRows.slice(start, start + pageSize);
  }, [filteredAndSortedRows, currentPage, pageSize]);

  const tablePagination = useMemo(
    () => ({ total: filteredAndSortedRows.length }),
    [filteredAndSortedRows.length]
  );

  const handleSorting = useCallback((next) => {
    setSortModel(next || []);
    setCurrentPage(1);
  }, []);

  const handleCustomPageChange = useCallback((page) => setCurrentPage(page), []);

  const handlePageSize = useCallback((e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  }, []);

  const handleTableChange = useCallback(
    (pag, _filters, sorter, extra) => {
      if (extra?.action === "sort") {
        const s     = Array.isArray(sorter) ? sorter[0] : sorter;
        const field = s?.field ?? s?.columnKey;
        if (s?.order && field) {
          handleSorting([{ field, sort: s.order === "ascend" ? "asc" : "desc" }]);
        } else {
          handleSorting([]);
        }
        return;
      }
      if (extra?.action === "paginate" && pag) {
        setCurrentPage(pag.current);
        setPageSize(pag.pageSize);
      }
    },
    [handleSorting]
  );

  const makeFields = useCallback(
    (row) => [
      { key: "lastName",    label: "Last Name",      value: row.lastName   ?? "-" },
      { key: "firstName",   label: "First Name",     value: row.firstName  ?? "-" },
      { key: "phoneNumber", label: "Cell Number",    value: formatPhone(row.phoneNumber) },
      { key: "residentKey", label: "Account Number", value: row.residentKey ?? "-" },
      { key: "address",     label: "Address",        value: row.address    ?? "-" },
      { key: "block",       label: "Block",          value: row.block      ?? "-" },
      { key: "lot",         label: "Lot",            value: row.lot        ?? "-" },
      {
        key: "sendStatus",
        label: "Status",
        value: (() => {
          const { label, variant } = getSendStatusInfo(row.sendStatus);
          return <Badge text={label} variant={variant} />;
        })(),
      },
    ],
    []
  );

  const onRow = useCallback(() => ({}), []);

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />;

  const createdDate = sms?.dateCreated ? dayjs(sms.dateCreated) : null;

  const sharedProps = {
    filteredRows: filteredAndSortedRows,
    antdColumns,
    currentPage,
    pageSize,
    handleTableChange,
    onRow,
    rows:                 pagedRows,
    pagination:           tablePagination,
    handleCustomPageChange,
    handlePageSize,
    handleSorting,
    sortModel,
    makeFields,
  };

  return (
    <Spin spinning={loading} size="large">
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.title}>Message Details</div>

          <div className={styles.formRow}>
            <div className={styles.field}>
              <span className={styles.label}>Sender Name</span>
              <Input value={sms?.senderName} disabled />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Property Name</span>
              <Input value={sms?.companyName} disabled />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Date Created</span>
              <DatePicker
                value={createdDate}
                disabled
                showTime={{ format: "hh:mm A" }}
                format="MM-DD-YYYY hh:mm A"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className={styles.messageBox}>
            <span className={styles.label}>Message</span>
            <TextArea
              rows={6}
              className={styles.textArea}
              value={sms?.messageContent}
              disabled
            />
          </div>

          <div className={styles.recipientsWrapper}>
            <div className={styles.recipientsTitle}>
              Recipients ({filteredAndSortedRows.length})
            </div>

            <Input
              data-testid="sms-recipient-filter-input"
              placeholder="Search by any field"
              value={recipientSearch}
              onChange={(e) => setRecipientSearch(e.target.value)}
              style={{ marginBottom: 16, width: 240 }}
              allowClear
              suffix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            />

            <ResponsiveStrategy Mobile={MobileView} Desktop={DesktopView} {...sharedProps} />
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default SMSDetailsView;